import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { ModuleContainer } from 'components/common/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { addRequirement, createConcept, fetchBrowserData } from './api';
import { ConceptTitleText } from './ConceptTitleText';
import { DetailsPanel } from './DetailsPanel';
import ModuleContext from 'CMFW/context/ModuleContext';
import TextEditor from 'CMFW/input/TextEditor';
import { BaseModel } from 'models/BaseModel';
import { ILayoutEdge, layoutTree, NODE_W } from './layout';
import { htmlTitleToMd } from './titleFormat';
import { IBrowserData, IConcept, LEVEL_NAMES, nodeState } from './types';

export type IProps = {
  courseId: number;
};

const DETAILS_PANEL_WIDTH = 410;
const LEVEL_HEADER_HEIGHT = 40;

const ConceptTree: React.FC<IProps> = (props) => {
  const { courseId } = props;
  const [data, setData] = useState<IBrowserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedKlines, setSelectedKlines] = useState<Set<number>>(new Set());
  const [klineSearch, setKlineSearch] = useState('');
  const [conceptSearch, setConceptSearch] = useState('');
  const [maxLevel, setMaxLevel] = useState<number>(4);
  const [showDropped, setShowDropped] = useState(true);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [hoveredEdge, setHoveredEdge] = useState<number | null>(null);
  // Drag-to-link state: drawing a new requirement edge between two cards.
  const [drag, setDrag] = useState<{
    from: string;
    side: 'left' | 'right';
    x: number;
    y: number;
    cx: number;
    cy: number;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [pendingLink, setPendingLink] = useState<{ prereq: string; dependent: string } | null>(null);
  const [linkNote, setLinkNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [newConcept, setNewConcept] = useState({ code: '', title: '', level: 1, klineId: 0, klinePos: '' });
  const canvasInnerRef = React.useRef<HTMLDivElement | null>(null);
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const dragEndedRef = React.useRef(false);
  const pendingCenterRef = React.useRef<string | null>(null);

  const editorContext = useMemo(() => new BaseModel({ render: () => {}, courseId }), [courseId]);

  const load = useCallback(async () => {
    const result = await fetchBrowserData(courseId);
    setData(result);
    setLoading(false);
  }, [courseId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedCode(null);
        setDrag(null);
        setDropTarget(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const rect = canvasInnerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDrag((d) => (d ? { ...d, cx: (e.clientX - rect.left) / zoom, cy: (e.clientY - rect.top) / zoom } : d));

      // Auto-scroll when the cursor nears a canvas edge, so a drag can reach
      // cards that are off-screen.
      const canvas = canvasRef.current;
      if (!canvas) return;
      const box = canvas.getBoundingClientRect();
      const MARGIN = 60;
      const SPEED = 18;
      if (e.clientY < box.top + MARGIN) canvas.scrollTop -= SPEED;
      else if (e.clientY > box.bottom - MARGIN) canvas.scrollTop += SPEED;
      if (e.clientX < box.left + MARGIN) canvas.scrollLeft -= SPEED;
      else if (e.clientX > box.right - MARGIN) canvas.scrollLeft += SPEED;
    };
    const onUp = () => {
      // A drop on a card resolves in the card's onMouseUp (fires first); this
      // cleans up drags released over empty canvas.
      setDrag(null);
      setDropTarget(null);
      dragEndedRef.current = true;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, zoom]);

  const startDrag = (e: React.MouseEvent, code: string, side: 'left' | 'right', x: number, y: number) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = canvasInnerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDrag({ from: code, side, x, y, cx: (e.clientX - rect.left) / zoom, cy: (e.clientY - rect.top) / zoom });
  };

  const dropOn = (targetCode: string) => {
    if (!drag || targetCode === drag.from) {
      setDrag(null);
      setDropTarget(null);
      return;
    }
    // Right port: "from" unlocks the target (target requires from).
    // Left port: "from" gets a new requirement (from requires target).
    const prereq = drag.side === 'right' ? drag.from : targetCode;
    const dependent = drag.side === 'right' ? targetCode : drag.from;
    setDrag(null);
    setDropTarget(null);
    dragEndedRef.current = true;
    const dep = byCode.get(dependent);
    if (dep?.requires.includes(prereq)) return; // already linked
    setLinkNote('');
    setPendingLink({ prereq, dependent });
  };

  const confirmLink = async () => {
    if (!pendingLink) return;
    const ok = await addRequirement(courseId, pendingLink.dependent, pendingLink.prereq, linkNote || undefined);
    setPendingLink(null);
    if (ok) await load();
  };

  const byCode = useMemo(() => {
    const map = new Map<string, IConcept>();
    data?.concepts.forEach((c) => map.set(c.code, c));
    return map;
  }, [data]);

  const visible = useMemo(() => {
    if (!data) return [];
    return data.concepts.filter((c) => {
      if (selectedKlines.size > 0 && (c.klineId === null || !selectedKlines.has(c.klineId))) return false;
      if (!showDropped && c.status === 'dropped') return false;
      return true;
    });
  }, [data, selectedKlines, showDropped]);

  const layout = useMemo(() => layoutTree(visible, byCode), [visible, byCode]);

  const selected = selectedCode ? byCode.get(selectedCode) ?? null : null;

  const centerConcept = useCallback(
    (code: string, behavior: ScrollBehavior = 'smooth') => {
      const canvas = canvasRef.current;
      const node = layout.nodes.find((item) => item.concept.code === code);
      if (!canvas || !node) return false;
      const coveredWidth = selectedCode ? DETAILS_PANEL_WIDTH : 0;
      const viewportWidth = Math.max(240, canvas.clientWidth - coveredWidth);
      canvas.scrollTo({
        left: Math.max(0, (node.x + NODE_W / 2) * zoom - viewportWidth / 2),
        top: Math.max(0, LEVEL_HEADER_HEIGHT + (node.y + node.height / 2) * zoom - canvas.clientHeight / 2),
        behavior,
      });
      return true;
    },
    [layout.nodes, selectedCode, zoom],
  );

  useEffect(() => {
    const code = pendingCenterRef.current;
    if (!code) return;
    const frame = window.requestAnimationFrame(() => {
      if (centerConcept(code)) pendingCenterRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [centerConcept]);

  const setBoardZoom = (nextZoom: number) => {
    pendingCenterRef.current = selectedCode;
    setZoom(Math.min(1.5, Math.max(0.4, nextZoom)));
  };

  const fitBoard = () => {
    const canvas = canvasRef.current;
    if (!canvas || layout.width === 0 || layout.height === 0) return;
    const coveredWidth = selectedCode ? DETAILS_PANEL_WIDTH : 0;
    const nextZoom = Math.min(
      1,
      (canvas.clientWidth - coveredWidth - 32) / layout.width,
      (canvas.clientHeight - 56) / layout.height,
    );
    setZoom(Math.max(0.4, nextZoom));
    window.requestAnimationFrame(() => canvas.scrollTo({ left: 0, top: 0, behavior: 'smooth' }));
  };

  // Codes connected to the selection: transitive requirements (ancestors) and
  // transitive dependents (descendants). Everything else gets dimmed.
  const related = useMemo(() => {
    if (!selectedCode || !byCode.has(selectedCode)) return null;
    const dependents = new Map<string, string[]>();
    byCode.forEach((c) => {
      c.requires.forEach((req) => {
        if (!dependents.has(req)) dependents.set(req, []);
        (dependents.get(req) as string[]).push(c.code);
      });
    });
    const set = new Set<string>([selectedCode]);
    const up = [selectedCode];
    while (up.length) {
      const code = up.pop() as string;
      (byCode.get(code)?.requires ?? []).forEach((req) => {
        if (!set.has(req)) {
          set.add(req);
          up.push(req);
        }
      });
    }
    const down = [selectedCode];
    while (down.length) {
      const code = down.pop() as string;
      (dependents.get(code) ?? []).forEach((dep) => {
        if (!set.has(dep)) {
          set.add(dep);
          down.push(dep);
        }
      });
    }
    return set;
  }, [selectedCode, byCode]);

  const stats = useMemo(() => {
    const counts = { placed: 0, eligible: 0, blocked: 0, gated: 0, dropped: 0 };
    visible.forEach((c) => {
      counts[nodeState(c, maxLevel)] += 1;
    });
    return counts;
  }, [visible, maxLevel]);

  const filteredKlines = useMemo(() => {
    if (!data) return [];
    const term = klineSearch.trim().toLowerCase();
    return term ? data.klines.filter((k) => k.name.toLowerCase().includes(term)) : data.klines;
  }, [data, klineSearch]);

  const searchMatches = useMemo(() => {
    const term = conceptSearch.trim().toLowerCase();
    if (term.length < 2 || !data) return [];
    return data.concepts
      .filter((c) => c.code.toLowerCase().includes(term) || c.title.toLowerCase().includes(term))
      .slice(0, 8);
  }, [conceptSearch, data]);

  const navigateTo = (code: string) => {
    const concept = byCode.get(code);
    if (!concept) return;
    // Make sure the target is visible, then select it.
    if (concept.status === 'dropped' && !showDropped) setShowDropped(true);
    if (concept.klineId !== null && !selectedKlines.has(concept.klineId)) {
      const next = new Set(selectedKlines);
      next.add(concept.klineId);
      setSelectedKlines(next);
    }
    pendingCenterRef.current = code;
    setSelectedCode(code);
    setConceptSearch('');
  };

  const suggestForKline = (klineId: number) => {
    if (!data || klineId === 0) return { code: '', klinePos: '' };
    let prefix = '';
    let maxMain = 0;
    let maxPos = 0;
    data.concepts.forEach((c) => {
      if (c.klineId !== klineId) return;
      const match = c.code.match(/^([a-z]+\d+)\.(\d+)/i);
      if (match) {
        prefix = match[1];
        maxMain = Math.max(maxMain, Number(match[2]));
      }
      maxPos = Math.max(maxPos, c.klinePos);
    });
    if (!prefix) return { code: '', klinePos: '' };
    return { code: `${prefix}.${maxMain + 1}`, klinePos: String(maxPos + 1) };
  };

  const submitNewConcept = async () => {
    const ok = await createConcept(courseId, {
      code: newConcept.code.trim(),
      title: htmlTitleToMd(newConcept.title),
      level: newConcept.level,
      klineId: newConcept.klineId || undefined,
      klinePos: newConcept.klinePos !== '' ? Number(newConcept.klinePos) : undefined,
    });
    if (ok) {
      setCreating(false);
      await load();
      navigateTo(newConcept.code.trim());
      setNewConcept({ code: '', title: '', level: 1, klineId: 0, klinePos: '' });
    }
  };

  const toggleKline = (id: number) => {
    const next = new Set(selectedKlines);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedKlines(next);
  };

  if (loading) {
    return (
      <ModuleContainer>
        <Center>
          <CircularProgress />
        </Center>
      </ModuleContainer>
    );
  }

  if (!data || data.count === 0) {
    return (
      <ModuleContainer>
        <Center>No concept tree for this course yet. Import one with the AI Assistant (importConceptTree).</Center>
      </ModuleContainer>
    );
  }

  return (
    <ModuleContainer>
      <Layout>
        <Sidebar>
          <SidebarTitle>
            Concept Tree · {data.count} concepts
            <SmallAction onClick={() => setCreating(true)}>+ new</SmallAction>
          </SidebarTitle>
          <TextField
            fullWidth
            size="small"
            placeholder="Find concept (code or title)…"
            value={conceptSearch}
            onChange={(e) => setConceptSearch(e.target.value)}
          />
          {searchMatches.length > 0 && (
            <SearchResults>
              {searchMatches.map((c) => (
                <SearchRow key={c.code} onClick={() => navigateTo(c.code)}>
                  <code>{c.code}</code> <ConceptTitleText text={c.title} />
                </SearchRow>
              ))}
            </SearchResults>
          )}

          <ControlRow>
            <span>Level gate</span>
            <Select value={maxLevel} onChange={(e) => setMaxLevel(Number(e.target.value))}>
              {[1, 2, 3, 4].map((level) => (
                <MenuItem key={level} value={level}>
                  up to {LEVEL_NAMES[level]}
                </MenuItem>
              ))}
            </Select>
          </ControlRow>
          <ControlRow>
            <span>Show dropped</span>
            <Checkbox size="small" checked={showDropped} onChange={(e) => setShowDropped(e.target.checked)} />
          </ControlRow>

          <Legend>
            <LegendItem $color="#2e7d32">placed ({stats.placed})</LegendItem>
            <LegendItem $color="#1565c0">unlocked ({stats.eligible})</LegendItem>
            <LegendItem $color="#e65100">blocked ({stats.blocked})</LegendItem>
            <LegendItem $color="#6a1b9a">level-gated ({stats.gated})</LegendItem>
            <LegendItem $color="#757575">dropped ({stats.dropped})</LegendItem>
            <LegendEdge $color="#c5c5c5">dependency</LegendEdge>
            <LegendEdge $color="#ffb74d">pending dependency</LegendEdge>
            <LegendGhost>outside selected KLines</LegendGhost>
          </Legend>

          <SidebarTitle>
            KLines
            <SmallAction onClick={() => setSelectedKlines(new Set())}>clear</SmallAction>
          </SidebarTitle>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter KLines…"
            value={klineSearch}
            onChange={(e) => setKlineSearch(e.target.value)}
          />
          <KlineList>
            {filteredKlines.map((k) => (
              <KlineRow key={k.id} onClick={() => toggleKline(k.id)}>
                <Checkbox size="small" checked={selectedKlines.has(k.id)} />
                <span>
                  <ConceptTitleText text={k.name} />
                </span>
              </KlineRow>
            ))}
          </KlineList>
        </Sidebar>

        <CanvasShell>
          <Canvas
            ref={canvasRef}
            onClick={() => {
              if (dragEndedRef.current) {
                dragEndedRef.current = false;
                return;
              }
              setSelectedCode(null);
            }}
          >
            {selectedKlines.size === 0 && visible.length > 300 ? (
              <Hint>
                {visible.length} concepts — select one or more KLines on the left to draw the tree (drawing everything
                at once is unreadable). The legend counts above already cover the whole course.
              </Hint>
            ) : (
              <>
                <StickyLevelHeaders style={{ width: layout.width * zoom }}>
                  {layout.columns.map((column) => (
                    <StickyLevelHeader key={column.level} style={{ left: column.x * zoom, width: column.width * zoom }}>
                      {LEVEL_NAMES[column.level] ?? `Level ${column.level}`}
                    </StickyLevelHeader>
                  ))}
                </StickyLevelHeaders>
                <ZoomStage style={{ width: layout.width * zoom, height: layout.height * zoom }}>
                  <CanvasInner
                    ref={canvasInnerRef}
                    style={{ width: layout.width, height: layout.height, transform: `scale(${zoom})` }}
                  >
                    {layout.columns.map((column) => (
                      <LevelColumn
                        key={column.level}
                        style={{ left: column.x, width: column.width, height: Math.max(0, layout.height - 24) }}
                      />
                    ))}
                    <svg width={layout.width} height={layout.height}>
                      <defs>
                        <ArrowMarker id="edge-arrow-gray" color="#c5c5c5" />
                        <ArrowMarker id="edge-arrow-amber" color="#ffb74d" />
                        <ArrowMarker id="edge-arrow-blue" color="#1565c0" />
                        <ArrowMarker id="edge-arrow-orange" color="#e65100" />
                      </defs>
                      {layout.edges.map((edge, i) => (
                        <path
                          key={`${edge.from}-${edge.to}`}
                          d={edgePath(edge)}
                          fill="none"
                          stroke={edgeColor(edge, selectedCode)}
                          strokeWidth={hoveredEdge === i ? 3 : edgeWidth(edge, selectedCode)}
                          markerEnd={`url(#${edgeMarkerId(edge, selectedCode)})`}
                          opacity={related && !(related.has(edge.from) && related.has(edge.to)) ? 0.12 : 1}
                          onMouseEnter={() => setHoveredEdge(i)}
                          onMouseLeave={() => setHoveredEdge(null)}
                        >
                          <title>{edgeTooltip(edge)}</title>
                        </path>
                      ))}
                    </svg>
                    {drag && (
                      <OverlaySvg width={layout.width} height={layout.height}>
                        <path
                          d={`M ${drag.x} ${drag.y} C ${(drag.x + drag.cx) / 2} ${drag.y}, ${(drag.x + drag.cx) / 2} ${
                            drag.cy
                          }, ${drag.cx} ${drag.cy}`}
                          fill="none"
                          stroke="#1565c0"
                          strokeWidth={2.5}
                          strokeDasharray="6 4"
                        />
                        <circle cx={drag.cx} cy={drag.cy} r={4} fill="#1565c0" />
                      </OverlaySvg>
                    )}
                    {drag && (
                      <DragLabel style={{ left: drag.cx + 14, top: drag.cy + 10 }}>
                        {drag.side === 'right'
                          ? `${drag.from} will be required by ${
                              dropTarget && dropTarget !== drag.from ? dropTarget : '…'
                            }`
                          : `${drag.from} will require ${dropTarget && dropTarget !== drag.from ? dropTarget : '…'}`}
                      </DragLabel>
                    )}
                    {layout.nodes.map((n) => {
                      const state = nodeState(n.concept, maxLevel);
                      return (
                        <Node
                          key={n.concept.code}
                          $state={state}
                          $ghost={n.ghost}
                          $dim={related !== null && !related.has(n.concept.code)}
                          $selected={n.concept.code === selectedCode}
                          $dropTarget={drag !== null && dropTarget === n.concept.code && n.concept.code !== drag.from}
                          style={{ left: n.x, top: n.y, width: NODE_W, height: n.height }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (dragEndedRef.current) {
                              dragEndedRef.current = false;
                              return;
                            }
                            setSelectedCode(n.concept.code);
                          }}
                          onMouseEnter={() => drag && setDropTarget(n.concept.code)}
                          onMouseLeave={() => drag && setDropTarget((t) => (t === n.concept.code ? null : t))}
                          onMouseUp={() => drag && dropOn(n.concept.code)}
                          title={n.concept.title}
                        >
                          <Port
                            $side="left"
                            title="Drag to add a requirement (what must come BEFORE this)"
                            onMouseDown={(e) => startDrag(e, n.concept.code, 'left', n.x, n.y + n.height / 2)}
                          />
                          <Port
                            $side="right"
                            title="Drag to make this a requirement of another concept (this comes FIRST)"
                            onMouseDown={(e) => startDrag(e, n.concept.code, 'right', n.x + NODE_W, n.y + n.height / 2)}
                          />
                          <NodeCode>
                            {n.concept.code}
                            {n.concept.placedLesson !== null && <NodeLesson>L{n.concept.placedLesson}</NodeLesson>}
                            {state === 'blocked' && <NodeLock>🔒</NodeLock>}
                            <NodeLevel>{LEVEL_NAMES[n.concept.level]}</NodeLevel>
                          </NodeCode>
                          <NodeTitle $dropped={n.concept.status === 'dropped'}>
                            <ConceptTitleText text={n.concept.title} />
                          </NodeTitle>
                        </Node>
                      );
                    })}
                  </CanvasInner>
                </ZoomStage>
              </>
            )}
          </Canvas>
          <CanvasToolbar onClick={(e) => e.stopPropagation()}>
            <ToolbarButton title="Zoom out" onClick={() => setBoardZoom(zoom - 0.1)}>
              −
            </ToolbarButton>
            <ZoomValue>{Math.round(zoom * 100)}%</ZoomValue>
            <ToolbarButton title="Zoom in" onClick={() => setBoardZoom(zoom + 0.1)}>
              +
            </ToolbarButton>
            <ToolbarButton title="Fit the complete board" onClick={fitBoard}>
              Fit
            </ToolbarButton>
            <ToolbarButton
              title="Center the selected concept"
              disabled={!selectedCode}
              onClick={() => selectedCode && centerConcept(selectedCode)}
            >
              Center
            </ToolbarButton>
          </CanvasToolbar>
        </CanvasShell>

        {selected && (
          <DetailsOverlay>
            <DetailsPanel
              courseId={courseId}
              concept={selected}
              byCode={byCode}
              klines={data.klines}
              maxLevel={maxLevel}
              targetDialectId={data.targetDialectId ?? null}
              onClose={() => setSelectedCode(null)}
              onChanged={load}
              onNavigate={navigateTo}
            />
          </DetailsOverlay>
        )}
        <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="sm">
          <DialogTitle>New concept</DialogTitle>
          <DialogContent>
            <FormRow>
              <Select
                value={newConcept.klineId}
                displayEmpty
                onChange={(e) => {
                  const klineId = Number(e.target.value);
                  const suggestion = suggestForKline(klineId);
                  setNewConcept({
                    ...newConcept,
                    klineId,
                    code: suggestion.code,
                    klinePos: suggestion.klinePos,
                  });
                }}
              >
                <MenuItem value={0}>— KLine —</MenuItem>
                {data.klines.map((k) => (
                  <MenuItem key={k.id} value={k.id}>
                    <ConceptTitleText text={k.name} />
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label="Position"
                placeholder="e.g. 6"
                value={newConcept.klinePos}
                onChange={(e) => setNewConcept({ ...newConcept, klinePos: e.target.value.replace(/[^0-9]/g, '') })}
              />
              <Select
                value={newConcept.level}
                onChange={(e) => setNewConcept({ ...newConcept, level: Number(e.target.value) })}
              >
                {[1, 2, 3, 4].map((level) => (
                  <MenuItem key={level} value={level}>
                    {LEVEL_NAMES[level]}
                  </MenuItem>
                ))}
              </Select>
              <CodePreview>{newConcept.code || '—'}</CodePreview>
            </FormRow>
            <TitleLabel>Title</TitleLabel>
            <EditorBox>
              <ModuleContext.Provider value={editorContext}>
                <TextEditor
                  key={creating ? 'new-concept' : 'closed'}
                  keyName={''}
                  config={{ toolBarInline: [['lang_target']] }}
                  text={newConcept.title}
                  onChangeText={(text) => setNewConcept((c) => ({ ...c, title: text }))}
                />
              </ModuleContext.Provider>
            </EditorBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={newConcept.code.trim() === '' || htmlTitleToMd(newConcept.title) === ''}
              onClick={submitNewConcept}
            >
              Create concept
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={pendingLink !== null} onClose={() => setPendingLink(null)}>
          <DialogTitle>New requirement</DialogTitle>
          {pendingLink && (
            <DialogContent>
              <LinkSentence>
                <code>{pendingLink.prereq}</code> must be taught <b>BEFORE</b> <code>{pendingLink.dependent}</code>
              </LinkSentence>
              <LinkDetail>
                <code>{pendingLink.prereq}</code>{' '}
                <ConceptTitleText text={byCode.get(pendingLink.prereq)?.title ?? ''} />
                <br />
                <code>{pendingLink.dependent}</code>{' '}
                <ConceptTitleText text={byCode.get(pendingLink.dependent)?.title ?? ''} />
              </LinkDetail>
              <TextField
                fullWidth
                autoFocus
                placeholder="Why? (optional note, saved on the edge)"
                value={linkNote}
                onChange={(e) => setLinkNote(e.target.value)}
              />
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={() => setPendingLink(null)}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={confirmLink}>
              Create requirement
            </Button>
          </DialogActions>
        </Dialog>
      </Layout>
    </ModuleContainer>
  );
};

const edgePath = (edge: ILayoutEdge): string => {
  if (edge.route === 'same-column') {
    const laneX = edge.laneX ?? edge.x1 + 28;
    return `M ${edge.x1} ${edge.y1} C ${laneX} ${edge.y1}, ${laneX} ${edge.y2}, ${edge.x2} ${edge.y2}`;
  }
  const midX = (edge.x1 + edge.x2) / 2;
  return `M ${edge.x1} ${edge.y1} C ${midX} ${edge.y1}, ${midX} ${edge.y2}, ${edge.x2} ${edge.y2}`;
};

const ArrowMarker: React.FC<{ id: string; color: string }> = ({ id, color }) => (
  <marker id={id} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
    <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
  </marker>
);

const edgeMarkerId = (edge: ILayoutEdge, selectedCode: string | null): string => {
  if (selectedCode && (edge.from === selectedCode || edge.to === selectedCode)) {
    return edge.pending ? 'edge-arrow-orange' : 'edge-arrow-blue';
  }
  return edge.pending ? 'edge-arrow-amber' : 'edge-arrow-gray';
};

const edgeTooltip = (edge: ILayoutEdge): string =>
  `${edge.from} → ${edge.to}\n${edge.pending ? 'Pending requirement' : 'Requirement satisfied'}${
    edge.note ? `\n${edge.note}` : ''
  }`;

const edgeColor = (edge: ILayoutEdge, selectedCode: string | null): string => {
  if (selectedCode && (edge.from === selectedCode || edge.to === selectedCode)) {
    return edge.pending ? '#e65100' : '#1565c0';
  }
  return edge.pending ? '#ffb74d' : '#c5c5c5';
};

const edgeWidth = (edge: ILayoutEdge, selectedCode: string | null): number =>
  selectedCode && (edge.from === selectedCode || edge.to === selectedCode) ? 2.5 : 1.2;

const Layout = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const Sidebar = styled.div`
  width: 260px;
  min-width: 260px;
  border-right: 1px solid #ddd;
  padding: 12px;
  overflow-y: auto;
  font-size: 13px;
  background: #fafafa;
`;

const SidebarTitle = styled.div`
  font-weight: 600;
  margin: 10px 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SmallAction = styled.span`
  font-weight: normal;
  font-size: 11px;
  color: #1565c0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const SearchResults = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  margin-top: 4px;
`;

const SearchRow = styled.div`
  padding: 4px 8px;
  cursor: pointer;
  &:hover {
    background: #e3f2fd;
  }
  code {
    font-weight: 600;
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const Legend = styled.div`
  margin: 12px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

const LegendItem = styled.span<{ $color: string }>`
  &::before {
    content: '●';
    color: ${(p) => p.$color};
    margin-right: 4px;
  }
`;

const LegendEdge = styled.span<{ $color: string }>`
  &::before {
    content: '→';
    color: ${(p) => p.$color};
    margin-right: 4px;
    font-size: 16px;
    font-weight: 700;
  }
`;

const LegendGhost = styled.span`
  &::before {
    content: '▢';
    color: #757575;
    margin-right: 4px;
  }
`;

const KlineList = styled.div`
  margin-top: 6px;
`;

const KlineRow = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    background: #eee;
  }
  span {
    font-size: 12.5px;
  }
`;

const CanvasShell = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
`;

const Canvas = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #fff;
`;

const ZoomStage = styled.div`
  position: relative;
`;

const CanvasInner = styled.div`
  position: relative;
  transform-origin: top left;
  svg {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
  svg path {
    pointer-events: stroke;
  }
`;

const StickyLevelHeaders = styled.div`
  position: sticky;
  top: 0;
  height: 40px;
  z-index: 4;
  pointer-events: none;
`;

const StickyLevelHeader = styled.div`
  position: absolute;
  top: 0;
  height: 40px;
  box-sizing: border-box;
  padding: 10px 16px;
  border: 1px solid #d8dee6;
  border-top: 0;
  background: rgba(248, 250, 252, 0.96);
  color: #52606d;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const LevelColumn = styled.div`
  position: absolute;
  top: 12px;
  box-sizing: border-box;
  border-left: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  background: rgba(248, 250, 252, 0.72);
  pointer-events: none;

  &:nth-child(even) {
    background: rgba(241, 245, 249, 0.72);
  }
`;

const CanvasToolbar = styled.div`
  position: absolute;
  top: 48px;
  right: 12px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px;
  border: 1px solid #d6dbe1;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
`;

const ToolbarButton = styled.button`
  min-width: 30px;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #344054;
  cursor: pointer;
  font: inherit;
  font-size: 12px;

  &:hover:not(:disabled) {
    background: #e3f2fd;
    color: #1565c0;
  }
  &:disabled {
    color: #b0b7c0;
    cursor: default;
  }
`;

const ZoomValue = styled.span`
  min-width: 42px;
  color: #667085;
  font-size: 12px;
  text-align: center;
`;

const DetailsOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 8;
  display: flex;
  box-shadow: -4px 0 14px rgba(0, 0, 0, 0.16);
`;

const Hint = styled.div`
  padding: 32px;
  color: #666;
  max-width: 560px;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #666;
`;

const NODE_COLORS: { [state: string]: { bg: string; border: string } } = {
  placed: { bg: '#e8f5e9', border: '#2e7d32' },
  eligible: { bg: '#e3f2fd', border: '#1565c0' },
  blocked: { bg: '#fff3e0', border: '#e65100' },
  gated: { bg: '#f3e5f5', border: '#6a1b9a' },
  dropped: { bg: '#f5f5f5', border: '#9e9e9e' },
};

const Port = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === 'left' ? 'left: -7px;' : 'right: -7px;')}
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #1565c0;
  border: 2px solid #fff;
  cursor: crosshair;
  opacity: 0;
  transition: opacity 0.12s;
  z-index: 2;
`;

const OverlaySvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 3;
`;

const DragLabel = styled.div`
  position: absolute;
  background: #263238;
  color: #fff;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 3;
`;

const EditorBox = styled.div`
  height: 84px;
`;

const TitleLabel = styled.div`
  font-size: 12px;
  color: #757575;
  margin-bottom: 4px;
`;

const CodePreview = styled.code`
  margin-left: auto;
  font-weight: 700;
  font-size: 15px;
  color: #555;
  padding-bottom: 6px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 14px;
  &:first-child {
    margin-top: 6px;
  }
`;

const LinkSentence = styled.div`
  font-size: 16px;
  margin-bottom: 10px;
  code {
    font-weight: 700;
  }
`;

const LinkDetail = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 14px;
  line-height: 1.5;
  code {
    font-weight: 600;
  }
`;

const Node = styled.div<{ $state: string; $ghost: boolean; $selected: boolean; $dim: boolean; $dropTarget: boolean }>`
  position: absolute;
  border: ${(p) => (p.$selected || p.$dropTarget ? '2.5px' : '1.5px')} ${(p) => (p.$ghost ? 'dashed' : 'solid')}
    ${(p) => (p.$dropTarget ? '#1565c0' : NODE_COLORS[p.$state].border)};
  background: ${(p) => NODE_COLORS[p.$state].bg};
  opacity: ${(p) => (p.$dim ? 0.25 : p.$ghost ? 0.75 : 1)};
  box-shadow: ${(p) => (p.$dropTarget ? '0 0 0 3px rgba(21, 101, 192, 0.25)' : 'none')};
  transition: opacity 0.15s;
  &:hover ${Port} {
    opacity: 1;
  }
  border-radius: 6px;
  padding: 7px 9px;
  cursor: pointer;
  overflow: hidden;
  font-size: 16.5px;
  &:hover {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
  }
`;

const NodeCode = styled.div`
  font-family: monospace;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
`;

const NodeLesson = styled.span`
  background: #2e7d32;
  color: #fff;
  border-radius: 9px;
  padding: 0 7px;
  font-size: 12px;
`;

const NodeLock = styled.span`
  font-size: 12px;
`;

const NodeLevel = styled.span`
  margin-left: auto;
  color: #666;
  font-size: 12px;
  font-family: sans-serif;
  font-weight: 400;
`;

const NodeTitle = styled.div<{ $dropped: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  text-decoration: ${(p) => (p.$dropped ? 'line-through' : 'none')};
  color: #333;
`;

export default ConceptTree;
